import { ConfigService } from '@app/configuration/config.service';

const URL_REGEX = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\b([-a-zA-Z0-9@:%_+.~#?&/=]*)/;

// Linear email check — avoids ReDoS by using indexOf instead of regex backtracking
function isEmail(value: string): boolean {
  const at = value.indexOf('@');
  if (at < 1) return false;
  const dot = value.indexOf('.', at + 2);
  return dot > at + 1 && dot < value.length - 1;
}

// Helper function to show link validation error
function showLinkError(editor: any, message: string): void {
  editor.selection.save();
  const $popup = editor.popups.get('link.insert');
  const $layer = $popup.find('.fr-link-insert-layer');
  let $error = $layer.find('.fr-link-error');

  if (!$error.length) {
    $error = editor.$(`<div class="fr-link-error" style="color: #d32f2f; font-size: 13px; margin-top: 5px;">${message}</div>`);
    $layer.append($error);
  } else {
    $error.text(message);
    $error.show();
  }
}

// Helper function to clear link validation error
function clearLinkError(editor: any): void {
  const $popup = editor.popups.get('link.insert');
  const $error = $popup?.find('.fr-link-error');
  $error?.hide();
}

// Ordered list numbering cycle by nesting depth (matches Quill's rendering behaviour)
const ORDERED_LIST_STYLES = ['decimal', 'lower-alpha', 'lower-roman'];

function getIndentLevel(li: Element): number {
  for (let i = 8; i >= 1; i--) {
    if (li.classList.contains(`ql-indent-${i}`)) { return i; }
  }
  return 0;
}

// Convert a Quill flat list container into properly nested ol/ul that Froala can edit.
function migrateQuillList(flatContainer: Element): Element {
  const items = Array.from(flatContainer.children) as HTMLElement[];
  const hasDataList = items.some(li => li.hasAttribute('data-list'));
  const hasQlIndent = items.some(li => Array.from(li.classList).some(c => c.startsWith('ql-indent-')));
  if (!hasDataList && !hasQlIndent) { return flatContainer; }

  // For old Quill content without data-list, infer list type from the container tag.
  const inferredType = flatContainer.tagName.toLowerCase() === 'ul' ? 'bullet' : 'ordered';

  // Compute the minimum indent level so the root list gets the correct
  // Quill-matching style (e.g. items starting at ql-indent-2 render as
  // lower-roman, not decimal). migrateQuillListsInRoot already splits groups
  // at top-level type/indent changes, so every group passed here shares a
  // consistent minimum indent.
  const minLevel = items.reduce((min, li) => Math.min(min, getIndentLevel(li)), 8);

  const stack: Array<{ listEl: HTMLElement; level: number; type: string }> = [];
  const firstType = items[0]?.getAttribute('data-list') || inferredType;
  const rootTag = firstType === 'bullet' ? 'ul' : 'ol';
  const root = document.createElement(rootTag);
  if (rootTag === 'ol') { root.style.listStyleType = ORDERED_LIST_STYLES[minLevel % ORDERED_LIST_STYLES.length]; }
  if (minLevel > 0) { root.style.paddingLeft = `${minLevel * 3}em`; }
  stack.push({ listEl: root, level: minLevel, type: firstType });

  for (const li of items) {
    const listType = li.getAttribute('data-list') || inferredType;
    const level = getIndentLevel(li);
    const tag = listType === 'bullet' ? 'ul' : 'ol';

    // Pop lists that are strictly deeper than the current level, or same level
    // but different type. Do NOT pop a same-level same-type list — siblings
    // must share the same <ol>/<ul> node.
    while (stack.length > 1) {
      const top = stack[stack.length - 1];
      if (top.level > level || (top.level === level && top.listEl.tagName.toLowerCase() !== tag)) {
        stack.pop();
      } else {
        break;
      }
    }

    const parent = stack[stack.length - 1];
    if (level > parent.level || (level === parent.level && tag !== parent.listEl.tagName.toLowerCase())) {
      const newList = document.createElement(tag);
      if (tag === 'ol') { newList.style.listStyleType = ORDERED_LIST_STYLES[level % ORDERED_LIST_STYLES.length]; }
      const lastLi = parent.listEl.lastElementChild;
      if (lastLi) { lastLi.appendChild(newList); } else { parent.listEl.appendChild(newList); }
      stack.push({ listEl: newList, level, type: listType });
    }

    const newLi = cloneAndSanitizeLi(li);
    newLi.querySelectorAll('.ql-ui').forEach(el => el.remove());
    stack[stack.length - 1].listEl.appendChild(newLi);
  }

  return root;
}

const QUILL_FONT_MAP: Record<string, string> = {
  'ql-font-monospace':  'Courier New,Courier,monospace',
  'ql-font-serif':      'Georgia,Times New Roman,serif',
  'ql-font-sans-serif': 'sans-serif'
};

const QUILL_ALIGN_MAP: Record<string, string> = {
  'ql-align-center':  'center',
  'ql-align-right':   'right',
  'ql-align-justify': 'justify'
};

const QUILL_SIZE_MAP: Record<string, string> = {
  'ql-size-small': '0.75em',
  'ql-size-large': '1.5em',
  'ql-size-huge':  '2.5em'
};

function migrateQuillFonts(root: Element): void {
  const selector = Object.keys(QUILL_FONT_MAP).map(c => `[class~="${c}"]`).join(',');
  root.querySelectorAll(selector).forEach((el: Element) => {
    const htmlEl = el as HTMLElement;
    for (const [cls, fontFamily] of Object.entries(QUILL_FONT_MAP)) {
      if (htmlEl.classList.contains(cls)) {
        if (!htmlEl.style.fontFamily) { htmlEl.style.fontFamily = fontFamily; }
        htmlEl.classList.remove(cls);
      }
    }
    if (!htmlEl.className) { htmlEl.removeAttribute('class'); }
  });
}

function splitIntoTypeGroups(items: HTMLElement[]): HTMLElement[][] {
  const groups: HTMLElement[][] = [];
  let current: HTMLElement[] = [];
  let currentType: string | null = null;
  for (const li of items) {
    const type = li.getAttribute('data-list') || 'bullet';
    const level = getIndentLevel(li);
    if (level === 0 && type !== currentType && current.length > 0) {
      groups.push(current);
      current = [];
    }
    if (level === 0) { currentType = type; }
    current.push(li);
  }
  if (current.length > 0) { groups.push(current); }
  return groups;
}

function cloneAndSanitizeLi(li: HTMLElement): HTMLElement {
  const newLi = document.createElement('li');
  Array.from(li.childNodes).forEach(child => newLi.appendChild(child.cloneNode(true)));
  newLi.querySelectorAll('script').forEach(el => el.remove());
  newLi.querySelectorAll('*').forEach(el => {
    Array.from(el.attributes)
      .filter(a => a.name.toLowerCase().startsWith('on'))
      .forEach(a => el.removeAttribute(a.name));
  });
  for (let i = 0; i <= 8; i++) { newLi.classList.remove(`ql-indent-${i}`); }
  if (!newLi.className) { newLi.removeAttribute('class'); }
  return newLi;
}

function migrateOldQuillList(container: Element): Element {
  const tag = container.tagName.toLowerCase() as 'ol' | 'ul';
  const items = Array.from(container.children) as HTMLElement[];
  const minLevel = items.reduce((min, li) => Math.min(min, getIndentLevel(li)), 8);
  const root = document.createElement(tag);
  if (tag === 'ol') { root.style.listStyleType = ORDERED_LIST_STYLES[minLevel % ORDERED_LIST_STYLES.length]; }
  if (minLevel > 0) { root.style.paddingLeft = `${minLevel * 3}em`; }
  const stack: Array<{ listEl: HTMLElement; level: number }> = [{ listEl: root, level: minLevel }];

  for (const li of items) {
    const level = getIndentLevel(li);
    while (stack.length > 1 && stack[stack.length - 1].level > level) { stack.pop(); }
    const parent = stack[stack.length - 1];
    if (level > parent.level) {
      const newList = document.createElement(tag);
      if (tag === 'ol') { newList.style.listStyleType = ORDERED_LIST_STYLES[level % ORDERED_LIST_STYLES.length]; }
      const lastLi = parent.listEl.lastElementChild;
      if (lastLi) { lastLi.appendChild(newList); } else { parent.listEl.appendChild(newList); }
      stack.push({ listEl: newList, level });
    }
    stack[stack.length - 1].listEl.appendChild(cloneAndSanitizeLi(li));
  }

  return root;
}

function migrateQuillListsInRoot(root: Element): void {
  const containers = Array.from(root.querySelectorAll(
    'ol:has(li[data-list]), ul:has(li[data-list]), ol:has(li[class*="ql-indent"]), ul:has(li[class*="ql-indent"])'
  ));
  for (const container of containers) {
    const items = Array.from(container.children) as HTMLElement[];
    const hasDataList = items.some(li => li.hasAttribute('data-list'));

    if (!hasDataList) {
      const migrated = migrateOldQuillList(container);
      container.parentNode?.replaceChild(migrated, container);
      continue;
    }

    const groups = splitIntoTypeGroups(items);
    if (groups.length <= 1) {
      const migrated = migrateQuillList(container);
      if (migrated !== container) { container.parentNode?.replaceChild(migrated, container); }
    } else {
      const fragment = document.createDocumentFragment();
      for (const group of groups) {
        const tempContainer = document.createElement(container.tagName);
        group.forEach(li => tempContainer.appendChild(li.cloneNode(true)));
        fragment.appendChild(migrateQuillList(tempContainer));
      }
      container.parentNode?.replaceChild(fragment, container);
    }
  }
}

function migrateQuillDirection(root: Element): void {
  root.querySelectorAll<HTMLElement>('[class~="ql-direction-rtl"]').forEach((el) => {
    if (!el.style.direction) { el.style.direction = 'rtl'; }
    if (!el.style.textAlign) { el.style.textAlign = 'inherit'; }
    el.classList.remove('ql-direction-rtl');
    if (!el.className) { el.removeAttribute('class'); }
  });
}

function migrateQuillAlign(root: Element): void {
  const selector = Object.keys(QUILL_ALIGN_MAP).map(c => `[class~="${c}"]`).join(',');
  root.querySelectorAll(selector).forEach((el: Element) => {
    const htmlEl = el as HTMLElement;
    for (const [cls, value] of Object.entries(QUILL_ALIGN_MAP)) {
      if (htmlEl.classList.contains(cls)) {
        if (!htmlEl.style.textAlign) { htmlEl.style.textAlign = value; }
        htmlEl.classList.remove(cls);
      }
    }
    if (!htmlEl.className) { htmlEl.removeAttribute('class'); }
  });
}

function migrateQuillSize(root: Element): void {
  const selector = Object.keys(QUILL_SIZE_MAP).map(c => `[class~="${c}"]`).join(',');
  root.querySelectorAll(selector).forEach((el: Element) => {
    const htmlEl = el as HTMLElement;
    for (const [cls, value] of Object.entries(QUILL_SIZE_MAP)) {
      if (htmlEl.classList.contains(cls)) {
        if (!htmlEl.style.fontSize) { htmlEl.style.fontSize = value; }
        htmlEl.classList.remove(cls);
      }
    }
    if (!htmlEl.className) { htmlEl.removeAttribute('class'); }
  });
}

function migrateQuillIndentedParagraphs(root: Element): void {
  for (let level = 1; level <= 8; level++) {
    root.querySelectorAll<HTMLElement>(`p.ql-indent-${level}, h1.ql-indent-${level}, h2.ql-indent-${level}, h3.ql-indent-${level}, h4.ql-indent-${level}, h5.ql-indent-${level}, h6.ql-indent-${level}`).forEach((node) => {
      node.style.marginLeft = `${level * 40}px`;
      node.classList.remove(`ql-indent-${level}`);
      if (!node.className) { node.removeAttribute('class'); }
    });
  }
}

// NOTE — ql-color-* / ql-bg-* classes (AC-3): Out of scope for structural migration.
// Modern Quill (1.x+) stores colors and backgrounds as inline styles (style="color:#...;
// background-color:#..."), NOT as CSS classes. No ql-color-* or ql-bg-* class mappings
// are required because the inline styles are preserved as-is by both Froala and DOMParser.

// NOTE — code block survival (AC-8): Content that uses <pre class="ql-syntax"> or
// .ql-code-block-container does NOT contain ql-indent or data-list attributes, so the
// early-exit check (no 'ql-' substring → return early) does NOT skip those blocks;
// they do contain the 'ql-' substring and will pass through DOMParser unchanged because
// none of the migration helpers target ql-syntax / ql-code-block classes.
// quill-compat.less previously provided styling for these elements; after its removal
// froala.less .fr-view styles and Froala's own <pre>/<code> rendering cover them correctly.

// Froala serializes content wrapped in a <div style="font-family: sans-serif;"> due to
// fontFamilyDefaultSelection inheriting into the editable div. Strip it on save so this
// artefact is never persisted to the database.
export function stripFroalaFontWrapper(html: string): string {
  if (!html || !html.startsWith('<div style="font-family:') && !html.startsWith('<div style="font-family: ')) { return html; }
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const first = doc.body.firstElementChild;
  if (first && first.tagName === 'DIV' && first.children.length > 0) {
    const style = (first as HTMLElement).style;
    if (style.fontFamily && style.length === 1) {
      return first.innerHTML;
    }
  }
  return html;
}

// Strips UserWay accessibility widget attributes injected into the editor DOM.
// UserWay mutates contenteditable elements with data-uw-* attributes which get
// serialized into the saved HTML if not removed before persisting to the database.
export function stripUserWayAttributes(html: string): string {
  if (!html?.includes('data-uw-')) { return html; }
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  doc.body.querySelectorAll<HTMLElement>('[data-uw-rm-heading],[data-uw-reader-element],[data-uw-rm-ignore],[data-uw-rm-empty-heading]').forEach(el => {
    el.removeAttribute('data-uw-rm-heading');
    el.removeAttribute('data-uw-reader-element');
    el.removeAttribute('data-uw-rm-ignore');
    el.removeAttribute('data-uw-rm-empty-heading');
  });
  // Strip any remaining data-uw-* attributes not covered by the targeted selectors
  doc.body.querySelectorAll('*').forEach(el => {
    Array.from(el.attributes)
      .filter(attr => attr.name.startsWith('data-uw-'))
      .forEach(attr => el.removeAttribute(attr.name));
  });
  return doc.body.innerHTML;
}

// Accepts an HTML string, migrates Quill-specific structures to Froala-native HTML,
// and returns the updated string. Runs off-DOM via DOMParser so no cursor disruption.
export function migrateQuillContent(html: string): string {
  if (!html || (!html.includes('ql-') && !html.includes('data-list'))) { return html; }
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  // Strip the ql-editor wrapper div if the content was saved with it — it carries
  // contenteditable="false" which makes the Froala editor non-editable.
  const qlEditor = doc.body.querySelector(':scope > .ql-editor');
  if (qlEditor) {
    while (qlEditor.firstChild) { doc.body.insertBefore(qlEditor.firstChild, qlEditor); }
    qlEditor.remove();
  }
  migrateQuillFonts(doc.body);
  migrateQuillAlign(doc.body);
  migrateQuillDirection(doc.body);
  migrateQuillSize(doc.body);
  migrateQuillListsInRoot(doc.body);
  migrateQuillIndentedParagraphs(doc.body);
  return doc.body.innerHTML;
}

const LINK_EVENTS = {
  'link.beforeInsert': function (href: string, text: string, attrs: object) {
    const editor = this;

    // Check if href is empty or just whitespace
    if (!href?.trim()) {
      showLinkError(editor, 'Please enter a valid URL');
      return false;
    }

    // Handle email addresses
    if (isEmail(href) && !href.startsWith('mailto:')) {
      clearLinkError(editor);
      editor.selection.restore();
      editor.link.insert('mailto:' + href, text, attrs);
      return false;
    }

    // Validate URL format — allow absolute http(s), mailto:, and root-relative paths
    // (e.g. /app/resource-download/<guid>). Reject protocol-relative URLs (//...) as
    // they bypass http(s) validation while appearing to be relative paths.
    const isRootRelative = href.startsWith('/') && !href.startsWith('//');
    if (!URL_REGEX.test(href) && !href.startsWith('mailto:') && !isRootRelative) {
      showLinkError(editor, 'Please enter a valid URL (e.g., https://example.com)');
      return false;
    }

    // Clear error and restore selection for valid links
    clearLinkError(editor);
    editor.selection.restore();
  },
  'commands.before': function (cmd: string) {
    const editor = this;

    // Intercept the linkOpen command to open in new tab
    if (cmd === 'linkOpen') {
      const $currentLink = editor.$(editor.link.get());
      if ($currentLink.length) {
        const href = $currentLink.attr('href');
        if (href) {
          window.open(href, '_blank', 'noopener,noreferrer');
        }
      }
      // Return false to prevent default behavior
      return false;
    }
  }
};

const FONT_FAMILY_CONFIG = {
  fontFamily: {
    'sans-serif': 'sans-serif',
    'Impact,Charcoal,sans-serif': 'Impact',
    'Tahoma,Geneva,sans-serif': 'Tahoma',
    'Times New Roman,Times,serif,-webkit-standard': 'Times New Roman',
    'Verdana,Geneva,sans-serif': 'Verdana',
    'Georgia,Times New Roman,serif': 'Serif',
    'Courier New,Courier,monospace': 'Monospace'
  },
  fontFamilyDefaultSelection: 'sans-serif',
  fontFamilySelection: true
};

// Paste configuration to preserve Quill classes and attributes
const PASTE_CONFIG = {
  // Preserve Quill-specific classes for backward compatibility
  htmlAllowedClasses: {
    'p': ['ql-indent-0', 'ql-indent-1', 'ql-indent-2', 'ql-indent-3',
          'ql-indent-4', 'ql-indent-5', 'ql-indent-6', 'ql-indent-7'],
    'li': ['ql-indent-0', 'ql-indent-1', 'ql-indent-2', 'ql-indent-3',
           'ql-indent-4', 'ql-indent-5', 'ql-indent-6', 'ql-indent-7', 'ql-indent-8'],
    'span':   ['ql-ui', 'ql-font-monospace', 'ql-font-serif', 'ql-font-sans-serif'],
    'strong': ['ql-font-monospace', 'ql-font-serif', 'ql-font-sans-serif'],
    'em':     ['ql-font-monospace', 'ql-font-serif', 'ql-font-sans-serif'],
    'u':      ['ql-font-monospace', 'ql-font-serif', 'ql-font-sans-serif'],
    's':      ['ql-font-monospace', 'ql-font-serif', 'ql-font-sans-serif']
  },
  // Preserve data-list attribute for Quill lists
  htmlAllowedAttrs: ['class', 'style', 'data-list', 'href', 'src', 'alt', 'title', 'contenteditable', 'target', 'rel'],
  // Don't remove any standard HTML tags
  htmlRemoveTags: [],
  // Preserve inline styles for colors, fonts, etc.
  pasteAllowedStyleProps: ['font-family', 'font-size', 'color', 'background-color', 'text-align',
                            'list-style-type', 'direction', 'text-decoration', 'font-weight',
                            'font-style', 'text-transform', 'margin-left', 'padding-left']
};

const PARAGRAPH_FORMAT_CONFIG = {
  paragraphFormat: {
    N: 'Normal',
    H1: 'Heading 1',
    H2: 'Heading 2',
    H3: 'Heading 3',
    H4: 'Heading 4',
    H5: 'Heading 5',
    H6: 'Heading 6'
  },
  paragraphFormatSelection: true
};

const FULL_TOOLBAR = [
  'bold', 'italic', 'underline', 'strikeThrough', '|',
  'subscript', 'superscript', '|',
  'fontSize', 'fontFamily', '|',
  'heading1', 'heading2', 'paragraphFormat', '|',
  'alignLeft', 'alignCenter', 'alignRight', 'alignJustify', '|',
  'formatOL', 'formatUL', '|',
  'outdent', 'indent', '|',
  'insertLink', '|',
  'quote', '|',
  'clearFormatting'
];

const DESCRIPTION_TOOLBAR = [
  'bold', 'italic', 'underline', 'strikeThrough', '|',
  'insertLink', '|',
  'quote', '|',
  'formatOL', 'formatUL', '|',
  'outdent', 'indent'
];

const TEXT_IMAGE_TOOLBAR = [
  'bold', 'italic', 'underline', 'strikeThrough', '|',
  'insertLink', '|',
  'quote', '|',
  'formatOL', 'formatUL', '|',
  'outdent', 'indent', '|',
  'uploadImage', 'resImage'
];

export { TEXT_IMAGE_TOOLBAR };

export class FroalaEditorConfig {
  private static get licenseKey(): string {
    const appConfig = (window as any).__APP_CONFIG__;
    const key = appConfig?.froalaLicenseKey
      ?? ConfigService.getInstance()?.getConfig()?.froalaLicenseKey;

    if (appConfig && key?.startsWith('${') && key?.endsWith('}')) {
      console.error(
        '[FroalaEditorConfig] Froala license key placeholder was not replaced during the build. ' +
        'Check the FROALA_LICENSE_KEY build argument in the Bamboo pipeline.'
      );
    }

    return key;
  }

  public static get options(): object {
    return {
      key: this.licenseKey,
      attribution: false,
      toolbarButtons: FULL_TOOLBAR,
      toolbarButtonsMD: FULL_TOOLBAR,
      toolbarButtonsSM: FULL_TOOLBAR,
      toolbarButtonsXS: FULL_TOOLBAR,
      toolbarSticky: false,
      linkAlwaysBlank: true,
      linkNoOpener: true,
      linkNoReferrer: true,
      linkEditButtons: ['linkOpen', 'linkEdit', 'linkRemove'],
      linkInsertButtons: [],
      wordCounterCount: false,
      charCounterCount: true,
      quickInsertEnabled: false,
      imageEditButtons: [],
      direction: 'auto',
      ...PARAGRAPH_FORMAT_CONFIG,
      ...FONT_FAMILY_CONFIG,
      ...PASTE_CONFIG,
      events: LINK_EVENTS
    };
  }

  public static get descriptionOptions(): object {
    return {
      key: this.licenseKey,
      attribution: false,
      toolbarButtons: DESCRIPTION_TOOLBAR,
      toolbarButtonsMD: DESCRIPTION_TOOLBAR,
      toolbarButtonsSM: DESCRIPTION_TOOLBAR,
      toolbarButtonsXS: DESCRIPTION_TOOLBAR,
      toolbarSticky: false,
      linkAlwaysBlank: true,
      linkNoOpener: true,
      linkNoReferrer: true,
      linkEditButtons: ['linkOpen', 'linkEdit', 'linkRemove'],
      linkInsertButtons: [],
      wordCounterCount: false,
      charCounterCount: true,
      quickInsertEnabled: false,
      imageEditButtons: [],
      ...FONT_FAMILY_CONFIG,
      ...PASTE_CONFIG,
      events: LINK_EVENTS
    };
  }

  public static get noToolbarOptions(): object {
    return {
      key: this.licenseKey,
      attribution: false,
      toolbarButtons: [],
      toolbarButtonsMD: [],
      toolbarButtonsSM: [],
      toolbarButtonsXS: [],
      toolbarVisibleWithoutSelection: false,
      wordCounterCount: false,
      charCounterCount: true,
      quickInsertEnabled: false,
      imageEditButtons: [],
      tabSpaces: 0,
      shortcutsEnabled: []
    };
  }
}
